interface EmailData {
  formData: {
    company: string
    name: string
    email: string
    phone: string
  }
  pricing: {
    oneTime: { min: number; max: number }
    monthly: { min: number; max: number }
    yearly: { min: number }
  }
  includedFeatures: Array<{ text: string; tooltip: string }>
  allAnswers?: Array<{ question: string; answer: string }>
  marketingConsent?: boolean
}

const styles = {
  container:
    'font-family: sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;',
  header:
    'background-color: #007AFF; padding: 30px; text-align: center; color: #ffffff;',
  content: 'padding: 30px; color: #333333; line-height: 1.6;',
  section:
    'margin-bottom: 30px; border-bottom: 1px solid #eeeeee; padding-bottom: 20px;',
  h1: 'margin: 0; font-size: 24px; font-weight: bold;',
  h2: 'color: #111827; font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #007AFF; display: inline-block; padding-bottom: 5px;',
  p: 'margin: 0 0 10px 0;',
  table: 'width: 100%; border-collapse: collapse; margin-bottom: 20px;',
  td: 'padding: 10px; border-bottom: 1px solid #f0f0f0;',
  tdLabel: 'font-weight: bold; width: 40%; color: #555;',
  priceBox:
    'background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; margin-bottom: 20px;',
  priceLabel:
    'font-size: 14px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;',
  priceValue: 'font-size: 24px; font-weight: bold; color: #0f172a;',
  featureList: 'list-style: none; padding: 0; margin: 0;',
  featureItem:
    'padding: 8px 0; border-bottom: 1px solid #f0f0f0; display: flex; align-items: center;',
  checkmark: 'color: #007AFF; font-weight: bold; margin-right: 10px;',
  footer:
    'background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #94a3b8; border-top: 1px solid #e2e8f0;',
  button:
    'display: inline-block; background-color: #007AFF; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: bold; margin-top: 20px;',
}

export const generateNoordDigitalEmailHTML = (data: EmailData): string => {
  const { formData, pricing, includedFeatures, allAnswers, marketingConsent } =
    data

  const answersHtml = allAnswers
    ?.map(
      (item) => `
      <div style="margin-bottom: 15px;">
        <div style="font-weight: bold; color: #333; margin-bottom: 4px;">${item.question}</div>
        <div style="color: #555; background: #f9f9f9; padding: 8px; border-radius: 4px;">${item.answer}</div>
      </div>
    `,
    )
    .join('')

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; background-color: #f4f4f5;">
      <div style="${styles.container}">
        <!-- Header -->
        <div style="${styles.header}">
          <h1 style="${styles.h1}">Nieuwe Prijsscan Aanvraag</h1>
          <p style="margin-top: 10px; opacity: 0.9;">${formData.company}</p>
        </div>

        <!-- Content -->
        <div style="${styles.content}">
          
          <!-- Contact Details -->
          <div style="${styles.section}">
            <h2 style="${styles.h2}">Contactgegevens</h2>
            <table style="${styles.table}">
              <tr><td style="${styles.td} ${styles.tdLabel}">Bedrijf</td><td style="${styles.td}">${formData.company}</td></tr>
              <tr><td style="${styles.td} ${styles.tdLabel}">Naam</td><td style="${styles.td}">${formData.name}</td></tr>
              <tr><td style="${styles.td} ${styles.tdLabel}">E-mail</td><td style="${styles.td}"><a href="mailto:${formData.email}" style="color: #007AFF;">${formData.email}</a></td></tr>
              <tr><td style="${styles.td} ${styles.tdLabel}">Telefoon</td><td style="${styles.td}">${formData.phone || 'Niet opgegeven'}</td></tr>
              <tr><td style="${styles.td} ${styles.tdLabel}">Marketing Consent</td><td style="${styles.td}">${marketingConsent ? 'Ja' : 'Nee'}</td></tr>
            </table>
          </div>

          <!-- Pricing -->
          <div style="${styles.section}">
            <h2 style="${styles.h2}">Prijsindicatie</h2>
            
            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Eenmalige investering</div>
              <div style="${styles.priceValue}">€${pricing.oneTime.min.toLocaleString()} – €${pricing.oneTime.max.toLocaleString()}</div>
            </div>

            ${
              pricing.monthly.min > 0
                ? `
            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Maandelijkse kosten</div>
              <div style="${styles.priceValue}">€${pricing.monthly.min} – €${pricing.monthly.max} <span style="font-size: 14px; font-weight: normal; color: #64748b;">p/m</span></div>
            </div>
            `
                : ''
            }

            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Jaarlijkse kosten</div>
              <div style="${styles.priceValue}">Vanaf €${pricing.yearly.min} <span style="font-size: 14px; font-weight: normal; color: #64748b;">p/j</span></div>
            </div>
          </div>

          <!-- Features -->
          <div style="${styles.section}">
            <h2 style="${styles.h2}">Inbegrepen Features</h2>
            <ul style="${styles.featureList}">
              ${includedFeatures
                .map(
                  (feature) => `
                <li style="${styles.featureItem}">
                  <span style="${styles.checkmark}">✓</span> ${feature.text}
                </li>
              `,
                )
                .join('')}
            </ul>
          </div>

          <!-- All Answers -->
          <div style="${styles.section}">
            <h2 style="${styles.h2}">Volledige Antwoorden</h2>
            ${answersHtml}
          </div>

        </div>

        <!-- Footer -->
        <div style="${styles.footer}">
          <p>Ontvangen op ${new Date().toLocaleString('nl-NL')}</p>
          <p>Noord Digital Prijsscan Systeem</p>
        </div>
      </div>
    </body>
    </html>
  `
}

export const generateCustomerEmailHTML = (data: EmailData): string => {
  const { formData, pricing, includedFeatures } = data

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; background-color: #f4f4f5;">
      <div style="${styles.container}">
        <!-- Header -->
        <div style="${styles.header}">
          <h1 style="${styles.h1}">Uw Persoonlijke Plan</h1>
          <p style="margin-top: 10px; opacity: 0.9;">Voor ${formData.company}</p>
        </div>

        <!-- Content -->
        <div style="${styles.content}">
          
          <p>Beste ${formData.name},</p>
          <p>Bedankt voor het invullen van de Website Scan bij Noord Digital! Hieronder vindt u uw persoonlijke prijsindicatie.</p>
          
          <!-- Pricing -->
          <div style="${styles.section}; margin-top: 30px;">
            <h2 style="${styles.h2}">Prijsindicatie</h2>
            
            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Eenmalige investering</div>
              <div style="${styles.priceValue}">€${pricing.oneTime.min.toLocaleString()} – €${pricing.oneTime.max.toLocaleString()}</div>
            </div>

            ${
              pricing.monthly.min > 0
                ? `
            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Maandelijkse kosten</div>
              <div style="${styles.priceValue}">€${pricing.monthly.min} – €${pricing.monthly.max} <span style="font-size: 14px; font-weight: normal; color: #64748b;">p/m</span></div>
            </div>
            `
                : ''
            }

            <div style="${styles.priceBox}">
              <div style="${styles.priceLabel}">Jaarlijkse kosten</div>
              <div style="${styles.priceValue}">Vanaf €${pricing.yearly.min} <span style="font-size: 14px; font-weight: normal; color: #64748b;">p/j</span></div>
            </div>
          </div>

          <!-- Features -->
          <div style="${styles.section}">
            <h2 style="${styles.h2}">Wat is inbegrepen?</h2>
            <ul style="${styles.featureList}">
              ${includedFeatures
                .map(
                  (feature) => `
                <li style="${styles.featureItem}">
                  <span style="${styles.checkmark}">✓</span> ${feature.text}
                </li>
              `,
                )
                .join('')}
            </ul>
          </div>

          <!-- Disclaimer -->
          <div style="background-color: #fffbeb; border-left: 4px solid #f59e0b; padding: 15px; margin-bottom: 30px; font-size: 14px; color: #92400e;">
            <strong>Let op:</strong> Dit is een indicatie. In de praktijk werken we deze prijs vaak naar beneden bij op basis van slimme keuzes en efficiënte werkwijzen.
          </div>

          <p>Wij nemen zo snel mogelijk contact met u op om deze indicatie vrijblijvend toe te lichten.</p>
          
          <a href="https://noorddigital.nl" style="${styles.button}">Bezoek onze website</a>

        </div>

        <!-- Footer -->
        <div style="${styles.footer}">
          <p>Noord Digital | <a href="mailto:info@noorddigital.nl" style="color: #94a3b8;">info@noorddigital.nl</a></p>
          <p><a href="https://noorddigital.nl" style="color: #94a3b8;">www.noorddigital.nl</a></p>
        </div>
      </div>
    </body>
    </html>
  `
}
